import { useCallback, useEffect, useRef, useState } from "react";
import useAuth from "../../hooks/useAuth";
import otpRecordsService from "../../services/otpRecords.service";
import PaginationBar from "../../components/common/PaginationBar";
import { PAGE_LIMIT } from "../../constants/pagination";
import { readListItems, readPagination } from "../../utils/listPagination";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import "../../styles/users.css";

const DEFAULT_TYPES = [
  { value: "LOGIN", label: "Get started" },
  { value: "COMPANY_REGISTRATION", label: "Registration" },
  { value: "FORGOT_PASSWORD", label: "Forgot Password" },
  { value: "REPO_ADMIN", label: "Repo Admin" },
  { value: "AGENT_REGISTRATION", label: "Agent Registration" },
  { value: "BANK_REGISTRATION", label: "Bank / NBFC Registration" },
  { value: "BANK_PERSON", label: "Bank / NBFC Person" },
  { value: "REPO_USER", label: "Repo User" },
  { value: "CASE_OTP", label: "Case OTP" },
];

const DEFAULT_STATUSES = [
  { value: "GENERATED", label: "Generated" },
  { value: "VERIFIED", label: "Verified" },
  { value: "EXPIRED", label: "Expired" },
  { value: "FAILED", label: "Failed" },
  { value: "USED", label: "Used" },
];

const EMPTY_FILTERS = {
  phone: "",
  type: "",
  status: "",
  from: "",
  to: "",
};

function formatTs(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
}

function reverseSerial(total, currentPage, pageSize, rowIndex) {
  return Math.max(
    Number(total || 0) - ((Number(currentPage || 1) - 1) * Number(pageSize || PAGE_LIMIT) + rowIndex),
    1
  );
}

function statusClass(status) {
  return `company-status company-status--${String(status || "").toLowerCase()}`;
}

export default function OtpRecordsPage({
  apiPath,
  fallbackNote,
}) {
  const { auth } = useAuth();
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState(DEFAULT_TYPES);
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
  const [otpNote, setOtpNote] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const isFirstLoad = useRef(true);

  const loadOtps = useCallback(
    async ({ silent = false } = {}) => {
      const showFullLoader = !silent && isFirstLoad.current;
      if (!auth?.token) {
        if (showFullLoader) setLoading(false);
        return;
      }

      try {
        if (showFullLoader) setLoading(true);
        if (!silent) setError("");

        const params = { page, limit: PAGE_LIMIT };
        if (applied.phone) params.phone = applied.phone;
        if (applied.type) params.type = applied.type;
        if (applied.status) params.status = applied.status;
        if (applied.from) params.from = applied.from;
        if (applied.to) params.to = applied.to;

        const res = await otpRecordsService.list(auth.token, params, apiPath);
        setItems(readListItems(res, ["items"]));
        setPagination(readPagination(res));
        const extra = res?.data && !Array.isArray(res.data) ? res.data : res;
        if (Array.isArray(extra?.types) && extra.types.length) setTypes(extra.types);
        if (Array.isArray(extra?.statuses) && extra.statuses.length) setStatuses(extra.statuses);
        if (extra?.otpNote) setOtpNote(extra.otpNote);
      } catch (err) {
        if (!silent) {
          setError(getApiErrorMessage(err, "Failed to load OTPs"));
          setItems([]);
        }
      } finally {
        if (showFullLoader) {
          setLoading(false);
          isFirstLoad.current = false;
        }
      }
    },
    [auth?.token, page, applied, apiPath]
  );

  useEffect(() => {
    const silent = !isFirstLoad.current;
    loadOtps({ silent });
  }, [loadOtps]);

  useEffect(() => {
    const timer = setInterval(() => {
      loadOtps({ silent: true });
    }, 20000);
    return () => clearInterval(timer);
  }, [loadOtps]);

  const handleApply = (event) => {
    event.preventDefault();
    setPage(1);
    setApplied({
      phone: draft.phone.trim(),
      type: draft.type,
      status: draft.status,
      from: draft.from,
      to: draft.to,
    });
  };

  const handleReset = () => {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(1);
  };

  return (
    <div className="page">
      <h2>OTPs</h2>
      <p className="ssdi-otp-note">
        {otpNote || fallbackNote}
      </p>

      <form className="company-search-panel" onSubmit={handleApply}>
        <div className="ssdi-otp-filters">
          <div className="ssdi-otp-filters__field">
            <label htmlFor="otp-phone">Search Mobile Number</label>
            <input
              id="otp-phone"
              type="search"
              value={draft.phone}
              onChange={(e) => setDraft((prev) => ({ ...prev, phone: e.target.value }))}
              placeholder="Mobile number"
            />
          </div>
          <div className="ssdi-otp-filters__field">
            <label htmlFor="otp-type">OTP Type</label>
            <select
              id="otp-type"
              value={draft.type}
              onChange={(e) => setDraft((prev) => ({ ...prev, type: e.target.value }))}
            >
              <option value="">All types</option>
              {types.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <div className="ssdi-otp-filters__field">
            <label htmlFor="otp-status">Status</label>
            <select
              id="otp-status"
              value={draft.status}
              onChange={(e) => setDraft((prev) => ({ ...prev, status: e.target.value }))}
            >
              <option value="">All statuses</option>
              {statuses.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <div className="ssdi-otp-filters__field">
            <label htmlFor="otp-from">From</label>
            <input
              id="otp-from"
              type="date"
              value={draft.from}
              onChange={(e) => setDraft((prev) => ({ ...prev, from: e.target.value }))}
            />
          </div>
          <div className="ssdi-otp-filters__field">
            <label htmlFor="otp-to">To</label>
            <input
              id="otp-to"
              type="date"
              value={draft.to}
              onChange={(e) => setDraft((prev) => ({ ...prev, to: e.target.value }))}
            />
          </div>
          <div className="ssdi-otp-filters__actions">
            <button type="submit" className="primary-page-btn">
              Search
            </button>
            <button type="button" className="secondary-page-btn" onClick={handleReset}>
              Reset
            </button>
          </div>
        </div>
      </form>

      {error && <p className="error-text">{error}</p>}

      {loading && items.length === 0 ? (
        <p className="muted">Loading OTPs…</p>
      ) : (
        <div className="company-table-wrap">
          <table className="users-table excel-grid-table">
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Mobile Number</th>
                <th>OTP</th>
                <th>Type</th>
                <th>User</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Expires At</th>
                <th>Attempts</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan="9">No OTP records match your filters.</td>
                </tr>
              ) : (
                items.map((row, index) => (
                  <tr key={row.id}>
                    <td>
                      {reverseSerial(
                        pagination.total,
                        pagination.page || page,
                        pagination.limit || PAGE_LIMIT,
                        index
                      )}
                    </td>
                    <td>{row.mobileNumber || "—"}</td>
                    <td>
                      <span
                        className="ssdi-otp-code"
                        title={
                          row.otpStored
                            ? "OTP sent to this mobile number"
                            : "OTP is not recoverable for this row"
                        }
                      >
                        {row.otp || "—"}
                      </span>
                    </td>
                    <td>{row.typeLabel || row.type || "—"}</td>
                    <td>
                      <div className="ssdi-otp-cell-user">
                        <span>{row.userName || "—"}</span>
                        {row.accountInfo ? <small>{row.accountInfo}</small> : null}
                      </div>
                    </td>
                    <td>
                      <span className={statusClass(row.status)}>
                        {row.statusLabel || row.status || "—"}
                      </span>
                    </td>
                    <td>{formatTs(row.createdAt)}</td>
                    <td>{formatTs(row.expiresAt)}</td>
                    <td>{Number(row.attempts || 0)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <PaginationBar
        page={pagination.page || page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        loading={loading}
        onPageChange={setPage}
      />
    </div>
  );
}
